import type { ConvexReactClient } from 'convex/react';
import type { Id } from '../../convex/_generated/dataModel';
import { api } from '../../convex/_generated/api';
import type { AudioChunk, ChunkProgress } from './ffmpeg';
import { chunkAudioFile } from './ffmpeg';

const XAI_TRANSCRIPTION_MODEL = 'x-ai/stt';
const XAI_SUPPORTED_FORMATS = new Set(['mp3', 'wav', 'ogg', 'opus', 'flac', 'aac', 'mp4', 'm4a', 'mkv']);
const OPENROUTER_SUPPORTED_FORMATS = new Set(['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'aiff']);

export interface UploadProgress {
  stage:
    | 'chunking'
    | 'uploading'
    | 'creating-meeting';
  /** Mensaje libre para mostrar en UI */
  message?: string;
  /** Progreso 0..1 si aplica */
  fraction?: number;
  /** Para stage="uploading", índice 1-based del chunk en curso */
  chunkIndex?: number;
  totalChunks?: number;
}

interface UploadedChunkRef {
  storageId: Id<'_storage'>;
  fileName: string;
  sizeBytes: number;
  format: string;
}

/**
 * Pipeline completo:
 *  1. Preparar audio: chunkear si hace falta y normalizar para proveedores estrictos
 *  2. Por cada chunk: pedir uploadUrl → POST → guardar storageId
 *  3. Crear meeting en la DB con todos los storageIds
 *
 * Devuelve el id del meeting creado para que el cliente se suscriba a su query.
 */
export interface UploadOptions {
  transcriptionModel?: string;
  summaryModel?: string;
  summaryContext?: string;
}

export async function uploadAndCreateMeeting(
  convex: ConvexReactClient,
  file: File,
  durationSec: number | null,
  onProgress: (p: UploadProgress) => void,
  options: UploadOptions = {}
): Promise<Id<'meetings'>> {
  const isXaiStt = options.transcriptionModel === XAI_TRANSCRIPTION_MODEL;
  const fileExt = (file.name.toLowerCase().split('.').pop() || '').replace(/[^a-z0-9]/g, '');
  const supportedFormats = isXaiStt ? XAI_SUPPORTED_FORMATS : OPENROUTER_SUPPORTED_FORMATS;
  const shouldNormalize = !supportedFormats.has(fileExt);

  // 1. Chunking
  const chunks: AudioChunk[] = await chunkAudioFile(
    file,
    (p: ChunkProgress) => {
      onProgress({
        stage: 'chunking',
        message: p.message,
        chunkIndex: p.chunkIndex,
        totalChunks: p.totalChunks,
      });
    },
    undefined,
    durationSec,
    shouldNormalize ? { forceMp3: true } : undefined
  );

  // 2. Upload de cada chunk a Convex storage
  const uploaded: UploadedChunkRef[] = [];
  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    onProgress({
      stage: 'uploading',
      chunkIndex: i + 1,
      totalChunks: chunks.length,
      fraction: i / chunks.length,
      message:
        chunks.length === 1
          ? 'Subiendo audio…'
          : `Subiendo fragmento ${i + 1} de ${chunks.length}…`,
    });

    const uploadUrl = await convex.mutation(api.meetings.generateUploadUrl, {});
    const res = await fetch(uploadUrl, {
      method: 'POST',
      headers: { 'Content-Type': chunk.blob.type || 'audio/mpeg' },
      body: chunk.blob,
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(
        `Falló la subida del fragmento ${i + 1}/${chunks.length}: ${res.status} ${res.statusText}. ${text}`
      );
    }

    const json = (await res.json()) as { storageId: Id<'_storage'> };
    uploaded.push({
      storageId: json.storageId,
      fileName: chunk.fileName,
      sizeBytes: chunk.sizeBytes,
      format: chunk.format,
    });
  }

  // 3. Crear meeting + chunks en la DB
  onProgress({
    stage: 'creating-meeting',
    message: 'Iniciando procesamiento…',
    fraction: 1,
  });

  const meetingId = await convex.mutation(api.meetings.createMeeting, {
    fileName: file.name,
    fileSizeBytes: file.size,
    durationSec: durationSec ?? undefined,
    transcriptionModel: options.transcriptionModel,
    summaryModel: options.summaryModel,
    summaryContext: options.summaryContext?.trim() || undefined,
    chunks: uploaded,
  });

  return meetingId;
}
